import {
  GetFinalizedPaymentProofsResponse,
  GetOcrResponse,
  SubmitPaymentProofsRequest,
} from '@diamond/shared/types';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteOcrPaymentProof,
  FINALIZED_PAYMENT_PROOF_QUERY_KEY,
  getFinalizedPaymentProofs,
  getOcrPaymentProofItems,
  OCR_PAYMENT_PROOF_QUERY_KEY,
  submitOcrPaymentProofs,
  uploadOcrPaymentProof,
} from '../queries/payment-proof-ocr';
import { NOTIFICATION_QUERY_KEY } from './useNotification';

export type OcrPaymentProofRefetchInterval =
  UseQueryOptions<GetOcrResponse>['refetchInterval'];
export type FinalizedPaymentProofRefetchInterval =
  UseQueryOptions<GetFinalizedPaymentProofsResponse>['refetchInterval'];

interface UseOcrPaymentProofItemsOptions {
  enabled?: boolean;
  refetchInterval?: OcrPaymentProofRefetchInterval;
}

export function useOcrPaymentProofItems(
  orderPaymentId: string,
  token: string,
  options?: UseOcrPaymentProofItemsOptions
) {
  return useQuery({
    queryKey: [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
    queryFn: () => getOcrPaymentProofItems(token, orderPaymentId),
    enabled: !!orderPaymentId && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useFinalizedPaymentProofs(
  orderPaymentId: string,
  token: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: [...FINALIZED_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
    queryFn: () => getFinalizedPaymentProofs(token, orderPaymentId),
    enabled: !!orderPaymentId && (options?.enabled ?? true),
  });
}

export function useUploadOcrPaymentProof(token: string) {
  return useMutation({
    mutationFn: ({
      orderPaymentId,
      files,
    }: {
      orderPaymentId: string;
      files: File[];
    }) => uploadOcrPaymentProof(token, orderPaymentId, files),
  });
}

export function useDeleteOcrPaymentProof(
  token: string,
  orderPaymentId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteOcrPaymentProof(token, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
      });
    },
  });
}

export function useSubmitOcrPaymentProofs(
  token: string,
  orderPaymentId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      paymentId,
      body,
    }: {
      paymentId: string;
      body: SubmitPaymentProofsRequest;
    }) => submitOcrPaymentProofs(token, paymentId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...OCR_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
      });
      queryClient.invalidateQueries({
        queryKey: [...FINALIZED_PAYMENT_PROOF_QUERY_KEY, orderPaymentId],
      });
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATION_QUERY_KEY],
      });
    },
  });
}
